import { getLocale, request } from '@umijs/max';
import { config } from '@/config/appConfig';

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

interface RequestConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  cache?: boolean;
  cacheTTL?: number;
  skipErrorHandler?: boolean;
  timeout?: number;
}

class HttpClient {
  private cache = new Map<string, CacheEntry>();
  private requestQueue = new Map<string, Promise<any>>();

  /**
   * Make API request with caching and error handling
   */
  async request<T = any>(requestConfig: RequestConfig): Promise<T> {
    const {
      url,
      method,
      data,
      params,
      headers = {},
      cache = true,
      cacheTTL = config.cache.defaultTTL,
      skipErrorHandler = false,
      timeout = config.api.timeout || 30000,
    } = requestConfig;

    // Add common headers
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    headers['X-Requested-With'] = 'XMLHttpRequest';

    // Browsers send Accept-Language as a list (e.g. "en-US,en;q=0.9"). Some APIs expect a
    // single BCP 47 tag matching app locale (en-US / ar-EG). Override unless caller set it.
    if (!headers['Accept-Language'] && !headers['accept-language']) {
      headers['Accept-Language'] = getLocale() || 'en-US';
    }

    // Create cache key
    const cacheKey = this.createCacheKey(url, method, params, data);

    // Check cache for GET requests
    if (method === 'GET' && cache) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    // Check if request is already in progress
    if (this.requestQueue.has(cacheKey)) {
      return this.requestQueue.get(cacheKey)!;
    }

    // Create request promise
    const requestPromise = this.executeRequest<T>({
      url,
      method,
      data,
      params,
      headers,
      skipErrorHandler,
      timeout,
    });

    // Add to queue
    this.requestQueue.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache successful GET requests
      if (method === 'GET' && cache && result) {
        this.setCache(cacheKey, result, cacheTTL);
      }

      return result;
    } finally {
      // Remove from queue
      this.requestQueue.delete(cacheKey);
    }
  }

  /**
   * Execute the actual request
   */
  private async executeRequest<T>(requestConfig: RequestConfig): Promise<T> {
    const { url, method, data, params, headers, skipErrorHandler, timeout } = requestConfig;

    try {
      const response = await request<T>(url, {
        method,
        data,
        params,
        headers,
        timeout,
        skipErrorHandler,
      });

      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create cache key from request parameters
   */
  private createCacheKey(
    url: string,
    method: string,
    params?: Record<string, any>,
    data?: any
  ): string {
    const keyData = {
      url,
      method,
      params: params || {},
      data: data || {},
    };
    return btoa(encodeURIComponent(JSON.stringify(keyData))); //encodeURIComponent for Arabic text handling
  }

  /**
   * Get data from cache
   */
  private getFromCache(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache
   */
  private setCache(key: string, data: any, ttl: number): void {
    // Check cache size limit
    if (this.cache.size >= config.cache.maxSize) {
      // Remove oldest entries
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, Math.floor(config.cache.maxSize * 0.2));
      toRemove.forEach(([key]) => this.cache.delete(key));
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear cache for specific pattern
   */
  clearCachePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; maxSize: number; hitRate: number } {
    return {
      size: this.cache.size,
      maxSize: config.cache.maxSize,
      hitRate: 0, // Would need to track hits/misses for accurate hit rate
    };
  }
}

export const httpClient = new HttpClient();
export default httpClient;
